import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Apple, Smartphone, Info, ExternalLink, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Visible only to users with an active IAP subscription (Apple or Google).
 * Stripe VIPs, gifted users, trial users, and free users do NOT see this.
 *
 * Why this exists: VIPs who bought through the App Store / Play Store can't
 * cancel from the web — Apple/Google require they manage subscriptions in
 * the device's native settings. Without this card, they get confused and
 * sometimes contact support thinking they're "locked in".
 */
export default function IapManageBanner({ user, isSpanish, dismissable = true }) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('cca_iap_banner_dismissed') === '1',
  );

  if (!user) return null;
  // We auto-unset iap_provider on REVOKE/EXPIRED webhook events, so its
  // presence is the truth signal — not the (sometimes-stale) subscription_status.
  const provider = user.iap_provider;
  if (!['apple', 'google'].includes(provider)) return null;
  if (dismissed && dismissable) return null;

  const isApple = provider === 'apple';

  const copy = isSpanish
    ? {
        title: isApple ? 'Administra tu VIP en iOS' : 'Administra tu VIP en Android',
        subtitle: isApple
          ? 'Tu suscripción fue comprada en la App Store. Apple requiere que la administres allí.'
          : 'Tu suscripción fue comprada en Google Play. Google requiere que la administres allí.',
        steps: isApple
          ? 'Ajustes → [tu nombre] → Suscripciones → ClearCommunicationApp'
          : 'Play Store → menú → Pagos y suscripciones → Suscripciones → ClearCommunicationApp',
        cta: isApple ? 'Abrir Ajustes de iOS' : 'Abrir Play Store',
        tooltip: 'Las reglas de la App Store / Play Store no nos permiten cancelar suscripciones desde la web.',
        autoRenewOff: 'Renovación automática desactivada — tu VIP terminará al final del periodo actual.',
        dismiss: 'No mostrar de nuevo',
      }
    : {
        title: isApple ? 'Manage your VIP in iOS' : 'Manage your VIP in Android',
        subtitle: isApple
          ? 'Your subscription was purchased through the App Store. Apple requires you to manage it there.'
          : 'Your subscription was purchased through Google Play. Google requires you to manage it there.',
        steps: isApple
          ? 'Settings → [your name] → Subscriptions → ClearCommunicationApp'
          : 'Play Store → menu → Payments & subscriptions → Subscriptions → ClearCommunicationApp',
        cta: isApple ? 'Open iOS Settings' : 'Open Play Store',
        tooltip: "App Store / Play Store rules don't allow us to cancel subscriptions from the web.",
        autoRenewOff: 'Auto-renew is off — your VIP will end at the current period.',
        dismiss: "Don't show again",
      };

  // Deep links. iOS opens the Subscriptions screen directly; Play deep-links
  // to the app's listing where the subscription is managed.
  const deepLink = isApple
    ? 'itms-apps://apps.apple.com/account/subscriptions'
    : 'https://play.google.com/store/account/subscriptions';

  const autoRenewOff = user.iap_auto_renew === false;

  const handleDismiss = () => {
    localStorage.setItem('cca_iap_banner_dismissed', '1');
    setDismissed(true);
  };

  const Icon = isApple ? Apple : Smartphone;
  const accent = isApple
    ? 'from-slate-900 to-slate-700 text-white'
    : 'from-emerald-600 to-teal-700 text-white';

  return (
    <Card
      className={`mb-6 border-0 shadow-md bg-gradient-to-br ${accent}`}
      data-testid="iap-manage-banner"
    >
      <CardContent className="p-4 sm:p-5 relative">
        {dismissable && (
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 opacity-70 hover:opacity-100 transition-opacity"
            aria-label={copy.dismiss}
            data-testid="iap-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="flex items-start gap-3 pr-6">
          <div className="rounded-full bg-white/15 p-2 flex-shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-base" data-testid="iap-banner-title">
                {copy.title}
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button aria-label="Why" className="opacity-70 hover:opacity-100">
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">{copy.tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm opacity-90 mb-2">{copy.subtitle}</p>
            <p className="text-xs opacity-80 font-mono bg-white/10 px-2 py-1.5 rounded mb-3 break-words">
              {copy.steps}
            </p>
            {autoRenewOff && (
              <p className="text-xs bg-amber-300/20 border border-amber-300/40 text-amber-100 px-2 py-1.5 rounded mb-3">
                ⚠️ {copy.autoRenewOff}
              </p>
            )}
            <Button
              asChild
              size="sm"
              variant="secondary"
              className="bg-white text-slate-900 hover:bg-white/90"
              data-testid="iap-banner-cta"
            >
              <a href={deepLink} target="_blank" rel="noreferrer">
                {copy.cta}
                <ExternalLink className="h-3 w-3 ml-1.5" />
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
